// src/components/Far117LimitsCard.js
//
// Dashboard card showing the FAR 117.23 cumulative flight time and FDP
// limits from /api/far117/cumulative: hours used in the current window,
// hours left, and where already-scheduled flying will peak — then the
// 117.25 rest rules: rest so far, when it's legal to report again, and any
// FDP short of rest. The duty periods behind it all can be expanded to
// check report and release times.

import { useEffect, useState } from 'react';
import { Gauge, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { format, parseISO, isAfter } from 'date-fns';

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
});

const STATUS_STYLES = {
  ok: { label: 'Within limit', className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300', bar: 'bg-green-500' },
  caution: { label: 'Near limit', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', bar: 'bg-amber-500' },
  projected: { label: 'Schedule exceeds', className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300', bar: 'bg-amber-500' },
  exceeded: { label: 'Exceeded', className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300', bar: 'bg-red-500' },
};

// Rest rules read as met / not met rather than as a limit
const REST_STATUS = {
  ok: { label: 'Met', className: STATUS_STYLES.ok.className },
  projected: { label: 'Schedule short', className: STATUS_STYLES.projected.className },
  exceeded: { label: 'Not met', className: STATUS_STYLES.exceeded.className },
};

const SOURCE_LABELS = { crewaccess: 'CrewAccess', manual: 'On Duty', estimated: 'Estimated' };
const RELEASE_LABELS = { crewaccess: 'CrewAccess', manual: 'Off Duty', estimated: 'Estimated' };

const LIMIT_TITLES = {
  flight672: 'Flight time · 672 hours',
  flight365: 'Flight time · 365 days',
  fdp168: 'FDP · 168 hours',
  fdp672: 'FDP · 672 hours',
};

const formatDateTime = (iso) => format(parseISO(iso), 'MMM d, HH:mm');
const formatDate = (iso) => format(parseISO(iso), 'MMM d');
const hours = (n) => `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 })} h`;

const StatusChip = ({ status, styles = STATUS_STYLES }) => {
  const style = styles[status] || styles.ok;
  return (
    <span className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${style.className}`}>
      {style.label}
    </span>
  );
};

const projectionText = (limit) => {
  if (limit.projectedExceedsAt) {
    return `Scheduled flying goes over ${hours(limit.limitHours)} on ${formatDateTime(limit.projectedExceedsAt)}`;
  }
  if (limit.projectedPeakAt) {
    return `Scheduled flying peaks at ${hours(limit.projectedPeakHours)} on ${formatDate(limit.projectedPeakAt)}`;
  }
  return null;
};

const LimitRow = ({ limit }) => {
  const style = STATUS_STYLES[limit.status] || STATUS_STYLES.ok;
  const share = Math.min(100, (limit.usedHours / limit.limitHours) * 100);
  const projection = projectionText(limit);
  return (
    <li className="py-3 border-b border-gray-100">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900">{LIMIT_TITLES[limit.key]}</p>
          <p className="text-xs text-gray-500">14 CFR {limit.rule} · {hours(limit.limitHours)} in any {limit.window}</p>
        </div>
        <StatusChip status={limit.status} />
      </div>
      <div
        className="mt-2 h-2 rounded-full bg-gray-100 overflow-hidden"
        role="meter"
        aria-label={LIMIT_TITLES[limit.key]}
        aria-valuemin={0}
        aria-valuemax={limit.limitHours}
        aria-valuenow={limit.usedHours}
      >
        <div className={`h-full rounded-full ${style.bar}`} style={{ width: `${share}%` }} />
      </div>
      <p className="mt-1 text-sm text-gray-700">
        <span className="font-medium">{hours(limit.usedHours)}</span> of {hours(limit.limitHours)}
        <span className="text-gray-400">{' · '}</span>
        {hours(limit.remainingHours)} left
      </p>
      {projection && (
        <p className={`text-xs ${limit.projectedExceedsAt ? 'text-red-700 font-medium' : 'text-gray-500'}`}>{projection}</p>
      )}
    </li>
  );
};

// "Shortest: 10.5 h before the Nov 3 report" / short FDPs first when there are any
const restRuleText = (rule) => {
  if (rule.shortfalls.length > 0) {
    return rule.shortfalls
      .map(s => `${hours(s.hours)} before the ${formatDateTime(s.reportAt)} report${s.upcoming ? ' (scheduled)' : ''}`)
      .join('; ');
  }
  if (rule.shortest) return `Shortest: ${hours(rule.shortest.hours)} before the ${formatDate(rule.shortest.reportAt)} report`;
  return 'No FDPs in the last 28 days or scheduled';
};

const RestSection = ({ rest, asOf }) => {
  let standing;
  if (rest.onDuty) {
    standing = `On duty · release ${formatDateTime(rest.releaseAt)}`;
  } else if (rest.lastReleaseAt) {
    const legalNow = rest.legalToReportAt && !isAfter(parseISO(rest.legalToReportAt), parseISO(asOf));
    standing = `Released ${formatDateTime(rest.lastReleaseAt)} · ${hours(rest.restSoFarHours)} rest so far · `
      + (legalNow ? 'legal to report now' : `legal to report from ${formatDateTime(rest.legalToReportAt)}`);
  }
  return (
    <div className="py-3 border-b border-gray-100">
      <h3 className="font-semibold text-gray-900">Rest</h3>
      {standing && <p className="text-sm text-gray-700">{standing}</p>}
      {rest.nextReportAt && (
        <p className="text-xs text-gray-500">Next report {formatDateTime(rest.nextReportAt)}</p>
      )}
      <ul className="mt-2 space-y-2">
        {rest.rules.map(rule => (
          <li key={rule.key} className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-gray-900">{rule.requiredHours} {rule.label}</p>
              <p className="text-xs text-gray-500">14 CFR {rule.rule}</p>
              <p className={`text-xs ${rule.shortfalls.length > 0 ? 'text-red-700 font-medium' : 'text-gray-500'}`}>
                {restRuleText(rule)}
              </p>
            </div>
            <StatusChip status={rule.status} styles={REST_STATUS} />
          </li>
        ))}
      </ul>
    </div>
  );
};

const DutyList = ({ fdps }) => (
  <ul className="mt-2 space-y-2">
    {[...fdps].reverse().map(f => (
      <li key={f.reportAt} className="text-sm">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-medium text-gray-900">
            {formatDateTime(f.reportAt)} – {format(parseISO(f.endAt), 'HH:mm')}
            {f.upcoming && <span className="ml-2 text-xs font-semibold text-sky-700">Scheduled</span>}
          </span>
          <span className="shrink-0 text-xs text-gray-500">
            {hours(f.hours)} FDP · {hours(f.flightHours)} block
          </span>
        </div>
        <p className="text-xs text-gray-500">
          Report: {SOURCE_LABELS[f.source]}
          <span className="text-gray-400">{' · '}</span>
          Release {format(parseISO(f.releaseAt), 'HH:mm')}: {RELEASE_LABELS[f.releaseSource]}
          {f.restBeforeHours !== null && (
            <>
              <span className="text-gray-400">{' · '}</span>
              <span className={f.restBeforeHours < 10 ? 'text-red-700 font-medium' : undefined}>
                {hours(f.restBeforeHours)} rest before
              </span>
            </>
          )}
        </p>
        <p className="text-xs text-gray-500">
          {f.legs.map(l => l.deadhead ? `${l.label} (DH)` : l.label).join(', ')}
        </p>
        {f.warnings.map(w => (
          <p key={w} className="text-xs text-amber-700">{w}</p>
        ))}
      </li>
    ))}
  </ul>
);

const Far117LimitsCard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [showDuties, setShowDuties] = useState(false);

  useEffect(() => {
    axios.get('/api/far117/cumulative', getAuthHeaders())
      .then(res => setData(res.data))
      .catch(err => {
        console.error('Error loading FAR 117 limits:', err);
        setError(true);
      });
  }, []);

  const title = (
    <h2 className="text-xl font-bold text-gray-900 flex items-center">
      <Gauge className="h-6 w-6 mr-2 text-sky-600" />
      FAR 117 limits
    </h2>
  );

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="mb-2">{title}</div>
        <p className="text-sm text-gray-500">Couldn't load FAR 117 limits right now.</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 animate-pulse">
        <div className="h-6 w-40 bg-gray-200 rounded mb-4"></div>
        <div className="h-4 w-full bg-gray-100 rounded mb-2"></div>
        <div className="h-4 w-3/4 bg-gray-100 rounded"></div>
      </div>
    );
  }

  const { asOf, limits, fdps, rest, sources, warnings } = data;
  const fdpWarnings = fdps.filter(f => f.warnings.length > 0).length;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      {/* On a narrow card the "as of" time drops below the title */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-2">
        {title}
        <span className="text-xs text-gray-500 whitespace-nowrap">as of {formatDateTime(asOf)}</span>
      </div>

      <ul>
        {limits.map(limit => <LimitRow key={limit.key} limit={limit} />)}
      </ul>

      {rest && <RestSection rest={rest} asOf={asOf} />}

      {fdps.length > 0 && (
        <div className="py-3">
          <button
            type="button"
            onClick={() => setShowDuties(v => !v)}
            aria-expanded={showDuties}
            className="w-full flex items-center justify-between gap-3 text-left"
          >
            <span>
              <span className="block font-semibold text-gray-900">Duty periods</span>
              <span className="block text-xs text-gray-500">
                Last 28 days and scheduled · report times: {sources.crewaccess} CrewAccess,{' '}
                {sources.manual} On Duty, {sources.estimated} estimated
              </span>
            </span>
            <span className="flex items-center gap-2 shrink-0">
              {fdpWarnings > 0 && <AlertTriangle className="h-4 w-4 text-amber-500" aria-label={`${fdpWarnings} to check`} />}
              {showDuties ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
            </span>
          </button>
          {showDuties && <DutyList fdps={fdps} />}
        </div>
      )}

      {warnings.length > 0 && (
        <ul className="mt-1 space-y-1">
          {warnings.map(w => <li key={w} className="text-xs text-amber-700">{w}</li>)}
        </ul>
      )}

      <p className="mt-3 text-xs text-gray-500 leading-relaxed">
        Flight time is block time from logged (or, until flown, scheduled) out and in
        times; deadheads aren't counted. An FDP runs from report to the last block-in.
        Report time is the On Duty you log on a flight, otherwise the CrewAccess roster,
        otherwise an hour before departure; release is your Off Duty, otherwise CrewAccess,
        otherwise 15 minutes after block-in. Rest only sees duty with flights — reserve and
        training aren't counted, and the 8-hour sleep opportunity, new-theater (117.25(c),
        (d)) and long-deadhead (117.25(g)) rest aren't checked. Times shown in your
        device's time zone. Confirm against your operator's records.
      </p>
    </div>
  );
};

export default Far117LimitsCard;
